import { useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';

/**
 * TradingView Advanced Chart embed.
 *
 * Uses TradingView's free, official embed widget (no key required, no login).
 * The widget loads via script injection — TradingView renders inside the iframe
 * it creates. We re-render on symbol/exchange/interval change by re-injecting
 * the script with new config.
 *
 * Pair-format mapping:
 *   haythix format:    "BTC/USDT" (CCXT-standard)
 *   TradingView format:  "BINANCE:BTCUSDT" (exchange-prefixed, no slash)
 *
 * Limitations of free widget:
 *   - Cannot programmatically place orders from drawings (TradingView Charting
 *     Library is paid-only for that)
 *   - Drawings don't persist across sessions for anonymous users
 *   - Limited to TradingView's exchange data sources
 */

// Map our exchange IDs → TradingView prefix.
// TradingView supports a subset; for unsupported exchanges we fall back to BINANCE
// (which has the broadest crypto pair coverage) so the chart still renders meaningfully.
const TV_EXCHANGE_PREFIX = {
  binance: 'BINANCE',
  binanceus: 'BINANCEUS',
  coinbase: 'COINBASE',
  kraken: 'KRAKEN',
  bybit: 'BYBIT',
  okx: 'OKX',
  kucoin: 'KUCOIN',
  gateio: 'GATEIO',
  bitget: 'BITGET',
  htx: 'HTX',
  cryptocom: 'CRYPTOCOM',
  bitfinex: 'BITFINEX',
  gemini: 'GEMINI',
  bitstamp: 'BITSTAMP',
  poloniex: 'POLONIEX',
  mexc: 'MEXC',
};

/**
 * Convert a haythix pair + exchange ID into a TradingView symbol string.
 * Examples:
 *   ('BTC/USDT', 'binance')     → 'BINANCE:BTCUSDT'
 *   ('ETH/USD', 'coinbase')     → 'COINBASE:ETHUSD'
 *   ('SOL/USDT:USDT', 'bybit')  → 'BYBIT:SOLUSDT.P'  (perp futures)
 *   (anything, unknown)         → 'BINANCE:<pair>'   (sensible fallback)
 */
export function pairToTradingViewSymbol(pair, exchangeId) {
  if (!pair) return 'BINANCE:BTCUSDT';
  const prefix = TV_EXCHANGE_PREFIX[exchangeId?.toLowerCase()] || 'BINANCE';

  // Detect perp futures format like "BTC/USDT:USDT" → BTCUSDT.P
  const isPerp = pair.includes(':');
  const cleaned = pair.split(':')[0].replace(/[/\-_]/g, '').toUpperCase();
  return isPerp ? `${prefix}:${cleaned}.P` : `${prefix}:${cleaned}`;
}

const VALID_INTERVALS = ['1', '5', '15', '30', '60', '240', 'D', 'W'];

/**
 * Renders a TradingView chart for a given symbol.
 *
 * @param {string} symbol      haythix pair format, e.g. "BTC/USDT"
 * @param {string} exchangeId  optional, e.g. "binance" — selects TV exchange
 * @param {string} interval    "1" | "5" | "15" | "30" | "60" | "240" | "D" | "W"
 * @param {string} theme       "dark" | "light"  (defaults to dark)
 * @param {number} height      pixels; if not set, fills container
 * @param {boolean} allowFullscreen  show expand-to-fullscreen toggle
 */
export default function TradingViewChart({
  symbol,
  exchangeId,
  interval = '60',
  theme = 'dark',
  height = 460,
  allowFullscreen = true,
}) {
  const containerRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);

  const tvSymbol = pairToTradingViewSymbol(symbol, exchangeId);
  const safeInterval = VALID_INTERVALS.includes(interval) ? interval : '60';

  useEffect(() => {
    if (!containerRef.current) return;

    // TradingView's embed-widget-advanced-chart.js script looks for a sibling
    // <div class="tradingview-widget-container__widget"> inside its parent
    // .tradingview-widget-container. The script also reads its own innerHTML
    // as the JSON config. The container_id field is NOT used by this widget
    // (that was the older tv.js API).
    containerRef.current.innerHTML = `
      <div class="tradingview-widget-container" style="height:100%;width:100%;">
        <div class="tradingview-widget-container__widget" style="height:100%;width:100%;"></div>
      </div>
    `;

    const widgetContainer = containerRef.current.querySelector('.tradingview-widget-container');
    if (!widgetContainer) return;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.type = 'text/javascript';
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: tvSymbol,
      interval: safeInterval,
      timezone: 'Etc/UTC',
      theme,
      style: '1',
      locale: 'en',
      toolbar_bg: theme === 'dark' ? '#0d0e14' : '#ffffff',
      enable_publishing: false,
      hide_legend: false,
      hide_side_toolbar: false,
      withdateranges: true,
      allow_symbol_change: true,
      details: true,
      hotlist: false,
      calendar: false,
      studies: ['Volume@tv-basicstudies'],
      support_host: 'https://www.tradingview.com',
    });

    widgetContainer.appendChild(script);

    return () => {
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [tvSymbol, safeInterval, theme]);

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[9990] bg-dark-900 flex flex-col">
        <div className="flex items-center justify-between p-3 bg-dark-800 border-b border-dark-700">
          <div className="text-xs font-mono text-gray-400">{tvSymbol} · {safeInterval}m</div>
          <button onClick={() => setFullscreen(false)} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-3 py-1 rounded-lg bg-dark-700 hover:bg-dark-600">
            <Minimize2 className="w-3.5 h-3.5" /> Exit fullscreen
          </button>
        </div>
        <div className="flex-1 relative">
          <div ref={containerRef} className="absolute inset-0" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-dark-900 rounded-xl border border-dark-700/50 overflow-hidden" style={{ height }}>
      {allowFullscreen && (
        <button
          onClick={() => setFullscreen(true)}
          className="absolute top-2 right-2 z-10 flex items-center gap-1 text-[10px] text-gray-400 hover:text-white px-2 py-1 rounded bg-dark-800/80 backdrop-blur border border-dark-600"
          title="Fullscreen"
        >
          <Maximize2 className="w-3 h-3" />
        </button>
      )}
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}
